package com.schoolconnect.app.designsystem

import androidx.compose.ui.graphics.Color

/**
 * Shared pastel tones for Super Admin / admin list summary metrics.
 */
data class AdminMetricTone(
    val accent: Color,
    val soft: Color
)

object AdminSummaryMetrics {
    /** Shared summary-card border (neutral, not tinted per card). */
    val CardBorder = Color(0xFFE2E8F0)

    // Named tones — soft fills match the approved dashboard palette.
    val SoftBlue = AdminMetricTone(Color(0xFF4A7DFF), Color(0xFFF7FAFF))
    val SoftLavender = AdminMetricTone(Color(0xFF7B5CDB), Color(0xFFFAF8FF))
    val SoftMint = AdminMetricTone(Color(0xFF2A9B8F), Color(0xFFF6FCF9))
    val SoftCream = AdminMetricTone(Color(0xFFD97706), Color(0xFFFFFAF6))

    // Legacy aliases used by older call sites / status dots.
    val Orange = SoftCream
    val Green = SoftMint
    val Pink = SoftLavender
    val Blue = SoftBlue
    val Purple = SoftLavender
    val Teal = SoftMint
    val Grey = AdminMetricTone(AppMuted, Color(0xFFF8F9FB))

    /**
     * Four soft fills in two-band shift order:
     * even rows A B C D, odd rows C D A B (no match beside or below).
     */
    val Cycle: List<AdminMetricTone> = listOf(SoftBlue, SoftLavender, SoftMint, SoftCream)

    fun gridTone(index: Int, columns: Int): AdminMetricTone {
        val cols = columns.coerceAtLeast(1)
        val row = index / cols
        val col = index % cols
        val bandIndex = if (row % 2 == 0) {
            col % Cycle.size
        } else {
            (col + 2) % Cycle.size
        }
        return Cycle[bandIndex]
    }

    /** Entity categories — keep these consistent across Super Admin screens. */
    val Schools = SoftBlue
    val Staff = SoftCream
    val Classes = SoftMint
    val Subjects = SoftLavender
    val Students = SoftBlue
    val Parents = SoftLavender
    val ItUsers = SoftMint
    val Total = SoftLavender

    /** Status metrics (Support tickets, Active/Inactive, etc.). */
    val StatusOpen = AdminMetricTone(AppDanger, AppDangerSoft)
    val StatusInProgress = AdminMetricTone(AppWarning, AppWarningSoft)
    val StatusResolved = AdminMetricTone(AppPrimary, AppPrimarySoft)
    val StatusClosed = Grey
    val StatusActive = SoftMint
    val StatusInactive = AdminMetricTone(AppDanger, AppDangerSoft)
}
